/* When a step or an answer fails, keep a note on this browser and send the
   same note to the payments server, which emails the Trasse team and stores
   it. The note is the ask, the step, the page, the time and the error.
   It does not include the message she was about to send: that can name a
   child. Phone numbers and other email addresses are stripped before it leaves. */

import { auth } from '../../firebase';
import { isPaymentsApiConfigured, paymentsApiUrl } from '../../utils/paymentsBaseUrl';

export interface FailureNote {
  orgCode: string;
  ask: string;
  step: string;
  tag?: string;
  page?: string;
  where?: string;
  phase?: string;
  error: string;
  path?: string;
  /** The plan's step titles, so the note shows what she was in the middle of. */
  ui?: string;
}

const LOCAL_KEY = 'trasse.zana.failures.v1';
const SEND_MS = 20000;

function scrub(s: string): string {
  return String(s || '')
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[email]')
    .replace(/\+?\d[\d\s-]{8,}\d/g, '[phone]')
    .slice(0, 500);
}

function keepLocal(note: FailureNote) {
  try {
    const prev = JSON.parse(localStorage.getItem(LOCAL_KEY) || '[]') as unknown[];
    const row = { ...note, at: new Date().toISOString() };
    const next = [row, ...(Array.isArray(prev) ? prev : [])].slice(0, 20);
    localStorage.setItem(LOCAL_KEY, JSON.stringify(next));
  } catch { /* storage blocked */ }
}

async function post(path: string, body: unknown): Promise<boolean> {
  if (!isPaymentsApiConfigured()) return false;
  const token = await auth.currentUser?.getIdToken().catch(() => null);
  if (!token) return false;
  const ac = new AbortController();
  const timer = setTimeout(() => ac.abort(), SEND_MS);
  try {
    const res = await fetch(paymentsApiUrl(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
      signal: ac.signal,
    });
    return res.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/** Save the failure here and send it to the team. Never throws. */
export async function reportZanaFailure(note: FailureNote): Promise<void> {
  const clean: FailureNote = {
    orgCode: scrub(note.orgCode).slice(0, 40),
    ask: scrub(note.ask),
    step: scrub(note.step),
    tag: scrub(note.tag || '').slice(0, 20),
    page: scrub(note.page || '').slice(0, 40),
    where: scrub(note.where || '').slice(0, 40),
    phase: scrub(note.phase || '').slice(0, 20),
    error: scrub(note.error),
    path: scrub(note.path || '').slice(0, 120),
    ui: scrub(note.ui || '').slice(0, 800),
  };
  keepLocal(clean);
  await post('/api/premium/zana/failure', clean);
}

/** Tell the server a job finished, so a later answer can see the receipt. */
export function postJobReceipt(orgCode: string, receipt: string): void {
  const text = scrub(receipt).slice(0, 380);
  if (!orgCode || text.length < 8) return;
  void post('/api/premium/zana/receipt', { orgCode, receipt: text });
}
